import { useEffect, useState, useRef } from 'react';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { DataTable } from 'primereact/datatable';
import { Panel } from 'primereact/panel';
import { Toast } from 'primereact/toast';
import { Menubar } from 'primereact/menubar';

import type {
  DataTableFilterEvent,
  DataTableFilterMeta,
  DataTableExpandedRows,
  DataTableRowExpandEvent,
  DataTableSortEvent,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { CustomerAllocation, CustomerDeposit } from '../../types';
import { allocationsApi, CustomerBalance } from '../../api';
import { usePersistentState, useAccountPreference, useAccountMenuItem } from '../../hooks';
import { formatDate } from '../../utils';

interface CustomerBalancesTabProps {
  clientId: string;
}

export const CustomerBalancesTab: React.FC<CustomerBalancesTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const [customers, setCustomers] = useState<CustomerBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const { selectedAccountId } = useAccountPreference(clientId);
  const { accountMenuItem } = useAccountMenuItem(clientId, { showAllOption: true });
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = usePersistentState<DataTableFilterMeta>(
    'reconciliation:customerBalances:filters',
    {
      customer_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    }
  );
  const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>();
  const [allocationMap, setAllocationMap] = useState<
    Record<string, CustomerAllocation[]>
  >({});
  const [depositMap, setDepositMap] = useState<Record<string, CustomerDeposit[]>>({});
  const [allocationLoading, setAllocationLoading] = useState<Record<string, boolean>>({});
  const [sortState, setSortState] = usePersistentState<{
    sortField: string | null;
    sortOrder: 1 | 0 | -1 | null | undefined;
  }>('reconciliation:customerBalances:sort', {
    sortField: null,
    sortOrder: null,
  });

  useEffect(() => {
    loadCustomers();
  }, [clientId, selectedAccountId]);

  useEffect(() => {
    setFilteredRowCount(customers.length);
  }, [customers]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await allocationsApi.getCustomerBalances(clientId, { accountId: selectedAccountId });
      setCustomers(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load customer balances');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.detail || 'Failed to load customer balances',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const onFilter = (event: DataTableFilterEvent) => {
    setFilters(event.filters);
    const filtered = event.filteredValue as CustomerBalance[] | null;
    setFilteredRowCount(filtered ? filtered.length : customers.length);
  };

  const handleSort = (event: DataTableSortEvent) => {
    setSortState({
      sortField: event.sortField ?? null,
      sortOrder: event.sortOrder ?? null,
    });
  };

  const handleRowExpand = (event: DataTableRowExpandEvent) => {
    const customer = event.data as CustomerBalance;

    if (allocationMap[customer.customer_id] || allocationLoading[customer.customer_id]) {
      return;
    }

    setAllocationLoading((prev) => ({ ...prev, [customer.customer_id]: true }));

    Promise.all([
      allocationsApi.getCustomerAllocations(clientId, customer.customer_id),
      allocationsApi.getCustomerUnassignedDeposits(clientId, customer.customer_id),
    ])
      .then(([allocations, deposits]) => {
        setAllocationMap((prev) => ({ ...prev, [customer.customer_id]: allocations }));
        setDepositMap((prev) => ({ ...prev, [customer.customer_id]: deposits }));
      })
      .catch((err) => {
        console.error('Failed to fetch data for customer', customer.customer_id, ':', err);
        setAllocationMap((prev) => ({ ...prev, [customer.customer_id]: [] }));
        setDepositMap((prev) => ({ ...prev, [customer.customer_id]: [] }));
      })
      .finally(() => {
        setAllocationLoading((prev) => ({ ...prev, [customer.customer_id]: false }));
      });
  };

  const formatCurrency = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) {
      return '-';
    }

    const numeric =
      typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));

    if (Number.isNaN(numeric)) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numeric);
  };


  const rowExpansionTemplate = (customer: CustomerBalance) => {
    const allocations = allocationMap[customer.customer_id] || [];
    const deposits = depositMap[customer.customer_id] || [];
    const isLoading = allocationLoading[customer.customer_id];

    if (isLoading) {
      return <ProgressBar mode="indeterminate" />;
    }

    if (allocations.length === 0 && deposits.length === 0) {
      return (
        <div className="row-expansion-container">
          <Message
            severity="info"
            text="No allocated ledger entries or unassigned deposits for this customer."
          />
        </div>
      );
    }

    return (
      <div className="row-expansion-container">
        {allocations.length > 0 && (
          <Panel header="Allocations">
            <DataTable value={allocations} size="small">
              <Column
                field="created_at"
                header="Date"
                style={{ width: '12%' }}
                body={(rowData: CustomerAllocation) => formatDate(rowData.created_at)}
              />
              <Column
                field="amount"
                header="Amount"
                style={{ width: '14%' }}
                body={(rowData: CustomerAllocation) => formatCurrency(rowData.amount)}
              />
              <Column field="ledger_entry_type" header="Entry Type" style={{ width: '15%' }} />
              <Column field="ledger_payment_type" header="Payment Type" style={{ width: '15%' }} />
              <Column field="allocation_source" header="Source" />
            </DataTable>
          </Panel>
        )}

        {deposits.length > 0 && (
          <Panel header="Unassigned Deposits">
            <DataTable value={deposits} size="small">
              <Column
                field="deposit_date"
                header="Date"
                style={{ width: '12%' }}
                body={(rowData: CustomerDeposit) => formatDate(rowData.deposit_date)}
              />
              <Column
                field="amount"
                header="Amount"
                style={{ width: '14%' }}
                body={(rowData: CustomerDeposit) => formatCurrency(rowData.amount)}
              />
              <Column field="customer_name" header="Customer Name" />
            </DataTable>
          </Panel>
        )}
      </div>
    );
  };

  const bodyTemplateBalance = (rowData: CustomerBalance) =>
    formatCurrency(rowData.balance);

  const menuItems = [accountMenuItem];

  return (
    <>
      <Toast ref={toast} />

      <Menubar className="actions-menubar" model={menuItems} />

      {error && <Message severity="error" text={error} />}

      {customers.length === 0 && !loading ? (
        <Message severity="info" text="No customers found." />
      ) : (
        <DataTable
          value={customers}
          dataKey="customer_id"
          size="small"
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          scrollable
          scrollHeight="flex"
          stripedRows
          loading={loading}
          filters={filters}
          filterDisplay="menu"
          onFilter={onFilter}
          totalRecords={filteredRowCount}
          sortField={sortState.sortField ?? undefined}
          sortOrder={sortState.sortOrder ?? undefined}
          onSort={handleSort}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
          rowExpansionTemplate={rowExpansionTemplate}
          onRowExpand={handleRowExpand}
          paginatorTemplate={{
            layout:
              'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
            CurrentPageReport: (options) => (
              <span className="paginator-current">
                Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> customers
              </span>
            ),
          }}
          emptyMessage="No customers found"
        >
          <Column expander style={{ width: '3%' }} />
          <Column
            field="customer_name"
            header="Customer"
            sortable
            filter
            filterPlaceholder="Search by name"
            showFilterMatchModes={false}
            style={{ width: '24%' }}
          />
          <Column
            field="balance"
            header="Balance"
            sortable
            style={{ width: '20%' }}
            body={bodyTemplateBalance}
          />
          <Column
            field="unallocated_deposits"
            header="Unallocated Deposits"
            sortable
            style={{ width: '22%' }}
            body={(rowData: CustomerBalance) => formatCurrency(rowData.unallocated_deposits)}
          />
          <Column
            field="transaction_count"
            header="# Transactions"
            sortable
          />
        </DataTable>
      )}
    </>
  );
};
